import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAllTenantsAdmin, createTenantAdmin, updateTenantAdmin } from '../services/adminApi';

const Toast = ({ message, type, visible }) => (
  <div style={{
    position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
    padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500,
    background: type === 'success' ? '#eaf3de' : type === 'error' ? '#fcebeb' : '#faeeda',
    color: type === 'success' ? '#3b6d11' : type === 'error' ? '#a32d2d' : '#854f0b',
    border: `0.5px solid ${type === 'success' ? '#97c459' : type === 'error' ? '#f09595' : '#fac775'}`,
    boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(8px)',
    transition: 'opacity 0.2s, transform 0.2s',
    pointerEvents: 'none',
    maxWidth: 360,
  }}>
    {message}
  </div>
);

const EMPTY_FORM = { name: '', slug: '', subdomain: '', address: '', phone: '', active: true };

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid #e5e7eb', fontSize: 14, fontFamily: 'DM Sans, sans-serif',
  boxSizing: 'border-box',
};

const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };

export default function AdminTenants() {
  const { token } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null); // null = create mode, else tenant id being edited
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success', visible: false });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 3000);
  }, []);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllTenantsAdmin(token);
      setTenants(data);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [token, showToast]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const handleChange = (field) => (e) => {
    const value = field === 'active' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [field]: value }));
  };

  const startEdit = (tenant) => {
    setEditingId(tenant.id);
    setForm({
      name: tenant.name || '',
      slug: tenant.slug || '',
      subdomain: tenant.subdomain || '',
      address: tenant.address || '',
      phone: tenant.phone || '',
      active: tenant.active,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast('Tenant name is required', 'error');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        const updated = await updateTenantAdmin(token, editingId, {
          name: form.name,
          slug: form.slug,
          subdomain: form.subdomain,
          address: form.address,
          phone: form.phone,
          active: form.active,
        });
        showToast(`Tenant "${updated.name}" updated.`, 'success');
        setEditingId(null);
      } else {
        const created = await createTenantAdmin(token, form);
        showToast(`Tenant "${created.name}" created — schema provisioned & default services seeded.`, 'success');
      }
      setForm(EMPTY_FORM);
      loadTenants();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const isEdit = !!editingId;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 20px', fontFamily: 'DM Sans, sans-serif' }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1e1b4b', marginBottom: 4 }}>
        {isEdit ? 'Edit Tenant' : 'Create Tenant'}
      </h2>
      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 24 }}>
        {isEdit
          ? 'Update this tenant\'s details. Changing the slug renames its underlying database schema — do this during a quiet period.'
          : 'Creates the tenant and immediately provisions its schema with default services — no restart needed.'}
      </p>

      <form onSubmit={handleSubmit} className="card-elevated" style={{ padding: 24, borderRadius: 14, marginBottom: 32 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Name *</label>
          <input style={inputStyle} value={form.name} onChange={handleChange('name')} placeholder="e.g. S4 Healthcare" required />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>
            {isEdit ? 'Slug (editing this renames the tenant\'s DB schema)' : 'Slug (optional — auto-generated from name if blank)'}
          </label>
          <input
            style={inputStyle}
            value={form.slug}
            onChange={handleChange('slug')}
            placeholder="e.g. s4healthcare"
          />
          {isEdit && (
            <div style={{ fontSize: 11, color: '#a32d2d', marginTop: 4 }}>
              Renames the Postgres schema in place. Make sure no requests are hitting this tenant while it saves.
            </div>
          )}
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Subdomain * (e.g. "manoj" for manoj.trimedcare.in)</label>
          <input
            style={inputStyle}
            value={form.subdomain}
            onChange={handleChange('subdomain')}
            placeholder="e.g. manoj"
            required
            pattern="[a-z0-9\-]{2,63}"
            title="2–63 lowercase letters, digits or hyphens"
          />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
            Lowercase letters, digits and hyphens only. The tenant's app is served only at this subdomain.
            No DNS or certificate changes are needed — *.trimedcare.in already resolves here.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Address</label>
            <input style={inputStyle} value={form.address} onChange={handleChange('address')} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Phone</label>
            <input style={inputStyle} value={form.phone} onChange={handleChange('phone')} />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13, color: '#374151' }}>
          <input type="checkbox" checked={form.active} onChange={handleChange('active')} />
          Active (provision schema immediately)
        </label>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: '12px 20px', fontSize: 14, fontWeight: 600, borderRadius: 10,
              border: 'none', background: '#4f46e5', color: '#fff',
              cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create Tenant'}
          </button>
          {isEdit && (
            <button
              type="button"
              onClick={cancelEdit}
              disabled={submitting}
              style={{
                padding: '12px 20px', fontSize: 14, fontWeight: 600, borderRadius: 10,
                border: '1px solid #e5e7eb', background: '#fff', color: '#374151',
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e1b4b', marginBottom: 12 }}>Existing Tenants</h3>
      {loading ? (
        <p style={{ fontSize: 13, color: '#6b7280' }}>Loading…</p>
      ) : tenants.length === 0 ? (
        <p style={{ fontSize: 13, color: '#6b7280' }}>No tenants yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tenants.map(t => (
            <div key={t.id} className="card-elevated" style={{
              padding: '12px 16px', borderRadius: 10, display: 'flex',
              justifyContent: 'space-between', alignItems: 'center', fontSize: 13,
            }}>
              <div>
                <strong style={{ color: '#1e1b4b' }}>{t.name}</strong>{' '}
                <span style={{ color: '#9ca3af' }}>({t.slug}{t.subdomain ? ` · ${t.subdomain}.trimedcare.in` : ''})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ color: t.active ? '#059669' : '#dc2626', fontWeight: 600 }}>
                  {t.active ? 'Active' : 'Inactive'}
                </span>
                <button
                  onClick={() => startEdit(t)}
                  style={{
                    padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8,
                    border: '1px solid #e5e7eb', background: '#fff', color: '#4f46e5',
                    cursor: 'pointer',
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Toast {...toast} />
    </div>
  );
}